import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ChevronLeft,
  Check,
  Plus,
  Upload,
  Trash2,
  Image as ImageIcon,
  X,
  FlaskConical,
  Layers,
  CircuitBoard,
  Factory,
  Cpu,
  Palette,
  Component,
  Lightbulb,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../Components/UI/button';
import { Input } from '../../Components/UI/input';
import { Textarea } from '../../Components/UI/textarea';
import { Label } from '../../Components/UI/label';
import { Dialog, DialogContent } from '../../Components/UI/dialog';
import { setGlobalLoading } from '../../Store/Actions';
import { getAgencyDetails } from '../../Store/Reducers';
import { updateCompanyProfile } from '../Settings/Profile/ProfileSectionAPI';
import { setPostAPI } from '../Community/FeedAPI';
import { segments, states, countries, cityToStateMap, SEGMENT_CONFIG } from '../../Components/Enums/DataEnums';
import useIsMobile from '../../Hooks/useIsMobile';
import CustomSearchableDropdown from '../../Components/Common/CustomSearchableDD';
import apiService from '../../Services/APIService';
import { cropToCenterSquare } from '../../Utils/ImageCompressor';
import { getUploadUrl } from '../../Utils/Utilities';
import { cn } from '../../Lib/Cn';

const SelectableChip = ({
  label,
  isSelected = false,
  onClick,
  disabled = false,
  className = '',
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex items-center justify-center h-8 text-[13px] font-medium px-3 rounded-[5px] border box-border transition-colors duration-150 cursor-pointer select-none whitespace-nowrap active:scale-[0.98]",
        isSelected
          ? "bg-white border-sahi-primary-color text-sahi-primary-color shadow-xs"
          : "bg-white text-[#374151] border-gray-200 hover:bg-gray-50",
        disabled && "opacity-40 cursor-not-allowed hover:bg-white active:scale-100",
        className
      )}
    >
      <span className="leading-none">{label}</span>
    </button>
  );
};

const SEGMENT_ICONS = {
  71: CircuitBoard, // PCB Manufacturing
  63: Component,    // EMS & Assembly Services
  91: Factory,      // Electronics Machinery
  58: Cpu,          // Components
  70: Layers,       // PCB Materials & Laminates
  56: FlaskConical, // Chemicals & Consumables
  60: Palette,      // Design Services
  66: Lightbulb,    // Lighting & LED Products
};

const SegmentOnboarding = ({ isOpen, setIsOpen, segment = 'pcb' }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isMobileDropdown = useIsMobile(640);

  const agencyData = useSelector(getAgencyDetails);

  const segDef = SEGMENT_CONFIG[segment] || SEGMENT_CONFIG.pcb;


  // Step state: 1 = Profile, 2 = Targets, 3 = Team
  const [step, setStep] = useState(1);

  // Segments listed strictly in segmentIDs maintaining the specified order
  const segmentIDs = segDef.segmentIDs || segDef.relevantSegmentIDs || [];
  const relevantSegments = segmentIDs
    .map((id) => segments.find((s) => s.SegmentID === id))
    .filter(Boolean);

  const stateOptions = states
    .filter((state) => state.CountryID === 1)
    .map((state) => ({
      value: state.StateID,
      label: state.State,
      code: state.Code,
    }));

  const countryOptions = countries.map((country) => ({
    value: country.CountryID,
    label: country.Country,
    code: country.CountryCode,
  }));

  const segmentIDFromRedux = useSelector(state => state.masterData.segmentID) || "";

  // Step 1: Profile State
  const [profileData, setProfileData] = useState(() => {
    const defaultSegID = segDef.defaultSegmentID || segmentIDs[0] || 71;
    const initialSegID = segmentIDFromRedux || defaultSegID;
    const matchedSeg = segments.find((s) => Number(s.SegmentID) === Number(initialSegID));

    return {
      logo: '',
      logoPreview: null,
      segmentID: initialSegID,
      industryID: matchedSeg?.IndustryID || 6,
      city: '',
      stateID: 0,
      state: '',
      countryID: 1,
      country: 'India',
    };
  });

  const [showLogoActions, setShowLogoActions] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (agencyData || segmentIDFromRedux) {
      const segID = segmentIDFromRedux || agencyData?.SegmentID || segDef.defaultSegmentID;
      const matchedSeg = segments.find((s) => Number(s.SegmentID) === Number(segID));
      const indID = matchedSeg?.IndustryID || agencyData?.IndustryID || "";
      setProfileData(prev => ({
        ...prev,
        logo: agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo || "",
        logoPreview: (agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo)
          ? ((agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo).startsWith("http")
            ? (agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo)
            : getUploadUrl(`Uploads/${agencyData?.Logo || agencyData?.LogoPath || agencyData?.logo}`))
          : null,
        segmentID: segID,
        industryID: indID,
        city: agencyData?.City || prev.city,
        stateID: agencyData?.StateID || prev.stateID,
        state: agencyData?.State || prev.state,
        countryID: agencyData?.CountryID || prev.countryID,
        country: agencyData?.Country || prev.country
      }));
    }
  }, [agencyData, segmentIDFromRedux, segDef]);

  // Logo handlers matching Onboarding.jsx
  const handleLogoChange = async (e) => {
    const rawFile = e.target.files?.[0];
    if (!rawFile) return;

    if (!rawFile.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, WEBP).");
      return;
    }

    if (rawFile.size > 10 * 1024 * 1024) {
      toast.error("Logo image size should be less than 10MB.");
      return;
    }

    dispatch(setGlobalLoading(true));
    try {
      const file = await cropToCenterSquare(rawFile, 1000, 0.9);

      if (file.size > 2 * 1024 * 1024) {
        toast.error("Logo image size should be less than 2MB.");
        return;
      }

      const uploadResult = await apiService.uploadFile(file, "Profile");
      if (uploadResult?.error) {
        toast.error(uploadResult.message || "Failed to upload logo image.");
        return;
      }

      const relativePath = uploadResult.data?.relativePath || uploadResult.data?.fileName || "";

      const payload = {
        changeType: "AGNLOGO",
        companyName: agencyData?.AgencyName || "",
        stateID: 0,
        taxID: "",
        logo: relativePath,
        banner: ""
      };

      const result = await updateCompanyProfile("AGNLOGO", payload);

      if (result?.error === false) {
        const fullLogoUrl = getUploadUrl(`Uploads/${relativePath}`);
        setProfileData((prev) => ({
          ...prev,
          logo: relativePath,
          logoPreview: fullLogoUrl
        }));
        toast.success("Logo updated successfully.");
      } else {
        toast.error(result?.message || "Failed to update logo.");
      }
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error("Failed to upload logo. Please try again.");
    } finally {
      dispatch(setGlobalLoading(false));
      if (e.target) e.target.value = "";
    }
  };

  const handleRemoveLogo = async () => {
    dispatch(setGlobalLoading(true));
    try {
      const payload = {
        changeType: "AGNLOGO",
        companyName: agencyData?.AgencyName || "",
        stateID: 0,
        taxID: "",
        logo: "",
        banner: ""
      };
      const result = await updateCompanyProfile("AGNLOGO", payload);
      if (result?.error === false) {
        setProfileData((prev) => ({
          ...prev,
          logo: "",
          logoPreview: null
        }));
        toast.success("Logo removed.");
      } else {
        toast.error(result?.message || "Failed to remove logo.");
      }
    } catch (error) {
      console.error("Error removing logo:", error);
      toast.error("Failed to remove logo.");
    } finally {
      dispatch(setGlobalLoading(false));
    }
  };

  // Step 2: BuyFrom & SellTo Segment mappings
  const [extraBuyFromSegments, setExtraBuyFromSegments] = useState([]);
  const [extraSellToSegments, setExtraSellToSegments] = useState([]);
  const [removedBuyFromIDs, setRemovedBuyFromIDs] = useState([]);
  const [removedSellToIDs, setRemovedSellToIDs] = useState([]);
  const [activeDropdown, setActiveDropdown] = useState(null); // 'buyFrom' | 'sellTo' | null

  const closeInlineDropdownOnBlur = (e) => {
    const wrapper = e.currentTarget;
    if (!wrapper) return;
    if (
      e.relatedTarget &&
      (wrapper.contains(e.relatedTarget) ||
        (typeof e.relatedTarget.closest === "function" &&
          e.relatedTarget.closest("[data-radix-popper-content-wrapper]")))
    ) {
      return;
    }
    setTimeout(() => {
      const active = document.activeElement;
      if (
        wrapper.contains(active) ||
        (active && typeof active.closest === "function" && active.closest("[data-radix-popper-content-wrapper]")) ||
        document.querySelector("[data-radix-popper-content-wrapper]")
      ) {
        return;
      }
      setActiveDropdown(null);
    }, 120);
  };

  // Reset the inline segment search whenever the step changes so it can only
  // be opened by the "Add" button, never carried over between steps.
  useEffect(() => {
    setActiveDropdown(null);
  }, [step]);

  // The inline segment search is only "active" while its list is open. Focus
  // its input as soon as it opens (so it is selected on Add press), and hide it
  // again as soon as the list closes or the user interacts somewhere outside.
  useEffect(() => {
    if (!activeDropdown) return;

    const focusTimer = setTimeout(() => {
      const input = document.querySelector("[data-radix-popper-content-wrapper] [cmdk-input]");
      if (input) {
        input.focus();
        if (typeof input.setSelectionRange === "function") {
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }
    }, 50);

    // The desktop dropdown renders its searchable list in a Radix popper.
    // Mobile renders a full-screen dialog with its own open/close handling.
    if (isMobileDropdown) {
      return () => clearTimeout(focusTimer);
    }

    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-inline-segment-dropdown]")) return;
      if (target.closest("[data-radix-popper-content-wrapper]")) return;
      setActiveDropdown(null);
    };

    const hasList = () => !!document.querySelector("[data-radix-popper-content-wrapper]");
    let hasOpened = hasList();
    let graceElapsed = false;
    const graceTimer = setTimeout(() => {
      graceElapsed = true;
    }, 400);
    const observer = new MutationObserver(() => {
      if (hasList()) {
        hasOpened = true;
        return;
      }
      if (hasOpened && graceElapsed) {
        setActiveDropdown(null);
      }
    });

    document.addEventListener("pointerdown", handlePointerDown, true);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(focusTimer);
      clearTimeout(graceTimer);
      document.removeEventListener("pointerdown", handlePointerDown, true);
      observer.disconnect();
    };
  }, [activeDropdown, isMobileDropdown]);

  // Step 4: Introduce Yourself / Initial Post
  const [postTitle, setPostTitle] = useState('');
  const [postDetail, setPostDetail] = useState('');
  const [postImageFile, setPostImageFile] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [hasCustomPostImage, setHasCustomPostImage] = useState(false);
  const [postSubmitting, setPostSubmitting] = useState(false);
  const postImageInputRef = useRef(null);

  // Sync post image with company logo if user hasn't picked a custom image
  useEffect(() => {
    if (!hasCustomPostImage) {
      if (profileData.logoPreview) {
        setPostImagePreview(profileData.logoPreview);
      } else {
        setPostImagePreview(null);
      }
    }
  }, [profileData.logoPreview, hasCustomPostImage]);

  const [hasCustomPostTitle, setHasCustomPostTitle] = useState(false);
  const [hasCustomPostDetail, setHasCustomPostDetail] = useState(false);

  // Set default intro title/details dynamically based on company name and selected segment
  useEffect(() => {
    const compName = agencyData?.AgencyName || "Our Company";
    if (!hasCustomPostTitle) {
      setPostTitle(`Introducing ${compName}`);
    }

    if (!hasCustomPostDetail) {
      const selectedSeg = segments.find((s) => Number(s.SegmentID) === Number(profileData.segmentID));
      const segmentName = selectedSeg?.Segment?.trim() || "";
      const specialization = segmentName
        ? `We specialize in ${segmentName}.`
        : `We specialize in quality manufacturing and reliable delivery.`;
      setPostDetail(`Hello everyone! We are pleased to join the Sahi B2B Network. ${specialization} Excited to connect and collaborate!`);
    }
  }, [agencyData?.AgencyName, profileData.segmentID, hasCustomPostTitle, hasCustomPostDetail]);

  const handlePostImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select a valid image file (PNG, JPG, WEBP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size should be less than 5MB.");
      return;
    }
    setHasCustomPostImage(true);
    setPostImageFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPostImagePreview(objectUrl);
  };

  // Cleanup helper on exit
  const cleanupAndFinish = (targetPostID = null) => {
    localStorage.setItem("onboarding_completed", "1");
    localStorage.setItem("isLoggedIn", "1");
    localStorage.removeItem("needs_onboarding");
    localStorage.removeItem("onboarding_segment");
    localStorage.removeItem("SignUp");

    const url = new URL(window.location.href);
    if (url.searchParams.has("onboarding")) {
      url.searchParams.delete("onboarding");
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }

    setIsOpen(false);
    if (targetPostID) {
      navigate(`/community?postID=${targetPostID}`);
    } else {
      navigate('/community');
    }
  };

  // Step 2 Save (Profile: Logo & Location)
  const handleSaveProfile = async () => {
    try {
      const selectedSegment = segments.find(s => s.SegmentID === Number(profileData.segmentID));
      const payload = {
        city: profileData.city?.trim() || "",
        stateID: profileData.stateID || 0,
        state: profileData.state?.trim() || "",
        countryID: profileData.countryID || 1,
        country: profileData.country?.trim() || "India",
        segmentID: profileData.segmentID || "",
        industryID: selectedSegment?.IndustryID || profileData.industryID || "",
        businessTypes: "",
        whatDoYouSell: "",
        businessTagline: "",
        description: "",
        googleMapsLink: ""
      };

      const result = await updateCompanyProfile("WEBSTORE", payload);
      if (result.error === false) {
        setStep(3); // Proceed to Step 3 (Supply Chain Targets)
      } else {
        toast.error(result.message || "Failed to update profile.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while saving profile.");
    }
  };

  // Step 3 Save (Targets - Buy From & Sell To)
  const handleSaveTargets = async () => {
    try {
      const currentSegmentObj = segments.find(s => Number(s.SegmentID) === Number(profileData.segmentID));
      const buyFromIDs = currentSegmentObj?.BuyFrom || [];
      const sellToIDs = currentSegmentObj?.SellTo || [];

      // Initial prefill strictly from the other 7 segments in relevantSegments
      const other7Segments = relevantSegments.filter(s => Number(s.SegmentID) !== Number(profileData.segmentID));
      const buyFromSegments = other7Segments
        .filter(s => buyFromIDs.includes(Number(s.SegmentID)))
        .filter(s => !removedBuyFromIDs.includes(Number(s.SegmentID)));
      const sellToSegments = other7Segments
        .filter(s => sellToIDs.includes(Number(s.SegmentID)))
        .filter(s => !removedSellToIDs.includes(Number(s.SegmentID)));

      const allBuyFromSegments = [
        ...buyFromSegments,
        ...extraBuyFromSegments.filter(extra => !buyFromSegments.some(s => Number(s.SegmentID) === Number(extra.SegmentID)))
      ];

      const allSellToSegments = [
        ...sellToSegments,
        ...extraSellToSegments.filter(extra => !sellToSegments.some(s => Number(s.SegmentID) === Number(extra.SegmentID)))
      ];

      const buyFromSegmentIDs = allBuyFromSegments.map(s => s.SegmentID).filter(Boolean);
      const buyFromIndustryIDs = [...new Set(allBuyFromSegments.map(s => s.IndustryID).filter(Boolean))];

      const sellToSegmentIDs = allSellToSegments.map(s => s.SegmentID).filter(Boolean);
      const sellToIndustryIDs = [...new Set(allSellToSegments.map(s => s.IndustryID).filter(Boolean))];

      const valuesPayload = [{
        SellToIndustryIDs: sellToIndustryIDs.join(','),
        SellToSegmentIDs: sellToSegmentIDs.join(','),
        BuyFromIndustryIDs: buyFromIndustryIDs.join(','),
        BuyFromSegmentIDs: buyFromSegmentIDs.join(',')
      }];

      const payload = {
        city: profileData.city?.trim() || "",
        stateID: profileData.stateID || 0,
        state: profileData.state?.trim() || "",
        countryID: profileData.countryID || 1,
        country: profileData.country?.trim() || "India",
        segmentID: profileData.segmentID || "",
        industryID: currentSegmentObj?.IndustryID || profileData.industryID || "",
        businessTypes: "",
        whatDoYouSell: "",
        businessTagline: "",
        description: "",
        googleMapsLink: "",
        values: JSON.stringify(valuesPayload)
      };

      const result = await updateCompanyProfile("WEBSTORE", payload);
      if (result.error === false) {
        setStep(4); // Proceed to Step 4 (Introduction Post)
      } else {
        toast.error(result.message || "Failed to update supply chain targets.");
      }
    } catch (err) {
      console.error(err);
      toast.error("An error occurred while saving supply chain targets.");
    }
  };

  // Step 4 Intro Post Publish Handler
  const handlePublishIntroPost = async () => {
    if (!postTitle.trim()) {
      toast.error("Please enter a title for your post.");
      return;
    }
    if (!postDetail.trim()) {
      toast.error("Please enter details for your post.");
      return;
    }
    if (!postImageFile && !(postImagePreview && profileData.logo)) {
      toast.error("Please attach a photo for your post.");
      return;
    }

    setPostSubmitting(true);
    dispatch(setGlobalLoading(true));
    try {
      let photoPath = "";
      if (postImageFile) {
        const uploadResult = await apiService.uploadFile(postImageFile, "Feed");
        if (uploadResult?.error) {
          throw new Error(uploadResult.message || "Image upload failed.");
        }
        photoPath = uploadResult?.data?.relativePath || uploadResult?.data?.fileName || "";
      } else if (postImagePreview && profileData.logo) {
        photoPath = profileData.logo;
      }

      const result = await setPostAPI({
        postID: 0,
        title: postTitle.trim(),
        detail: postDetail.trim(),
        postTypeID: 'A', // 'A' = Introduction category in POST_CATEGORIES
        photos: photoPath,
        isDeleted: 0
      });

      let createdPostID = null;
      let isPostSuccess = false;
      let sawSuccessFlag = false;
      try {
        const parsed = typeof result?.updatePackage === "string" ? JSON.parse(result.updatePackage) : result?.updatePackage;

        // `setPost` can return the created row in any `Table`/`TableN`, as an array or a
        // single object, so flatten them all before reading Success / PostID.
        const rows = [];
        if (Array.isArray(parsed)) {
          rows.push(...parsed);
        } else if (parsed && typeof parsed === "object") {
          Object.keys(parsed).forEach((key) => {
            if (key === "Table" || key.startsWith("Table")) {
              const table = parsed[key];
              if (Array.isArray(table)) rows.push(...table);
              else if (table && typeof table === "object") rows.push(table);
            }
          });
        }

        const normalizeSuccess = (value) =>
          value === true ||
          value === 1 ||
          ["1", "true", "success"].includes(String(value).trim().toLowerCase());

        const successRow = rows.find((row) => row && (row.Success !== undefined || row.IsSuccess !== undefined));
        const idRow = rows.find(
          (row) => row && (row.PostID !== undefined || row.postID !== undefined || row.PostId !== undefined || row.RetData !== undefined)
        );

        const rawSuccess = successRow?.Success ?? successRow?.IsSuccess ?? parsed?.Success ?? result?.Success;
        sawSuccessFlag = rawSuccess !== undefined;
        // The API signals success either with a Success/IsSuccess column or with
        // `{ error: false, message: "Success" }` (same convention used elsewhere).
        isPostSuccess =
          normalizeSuccess(rawSuccess) ||
          normalizeSuccess(parsed?.message) ||
          normalizeSuccess(result?.message);

        // `setPost` often returns the created PostID through `RetData` as a JSON string,
        // array, or object (e.g. `[{"PostID":46}]`), so unwrap it down to a plain scalar.
        const extractPostID = (value) => {
          let current = value;
          for (let depth = 0; depth < 5; depth++) {
            if (typeof current === "string") {
              const trimmed = current.trim();
              if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
                try {
                  current = JSON.parse(trimmed);
                  continue;
                } catch {
                  break;
                }
              }
              break;
            }
            if (Array.isArray(current)) {
              if (!current.length) {
                current = null;
                break;
              }
              current = current[0];
              continue;
            }
            if (current && typeof current === "object") {
              current = current.PostID ?? current.postID ?? current.PostId ?? current.RetData ?? current.ID ?? current.id ?? null;
              continue;
            }
            break;
          }
          if (current === undefined || current === null) return null;
          const str = String(current).trim();
          if (str === "" || str === "0" || str.toLowerCase() === "null" || str.toLowerCase() === "undefined") return null;
          return str;
        };

        const rawID = idRow?.PostID ?? idRow?.postID ?? idRow?.PostId ?? idRow?.RetData ?? parsed?.PostID ?? parsed?.RetData ?? result?.RetData ?? null;
        createdPostID = extractPostID(rawID);
      } catch (e) {
        console.error("Error extracting post result from setPost response:", e);
      }

      // If the response carried no explicit success flag at all but returned a real
      // PostID, treat the creation as successful so the post still opens.
      if (!sawSuccessFlag && !isPostSuccess && createdPostID) {
        isPostSuccess = true;
      }

      // Only open the created post when the API explicitly reports Success.
      // Otherwise finish the onboarding and land on the community feed.
      const postToOpen = isPostSuccess && createdPostID ? createdPostID : null;
      if (isPostSuccess) {
        toast.success("Introduction post published to the community!");
      }
      cleanupAndFinish(postToOpen);
    } catch (err) {
      console.error("Error publishing intro post:", err);
      toast.error("Failed to publish introduction: " + (err.message || "Unknown error"));
    } finally {
      setPostSubmitting(false);
      dispatch(setGlobalLoading(false));
    }
  };

  // Progress Bar with 4 steps
  const ProgressBar = () => (
    <div className="w-full space-y-3 mb-6 text-left">
      <div className="flex gap-2 w-full">
        <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 1 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
        <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 2 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
        <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 3 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
        <div className={`h-1.5 flex-1 rounded-full transition-all duration-500 ${step >= 4 ? 'bg-sahi-primary-color' : 'bg-zinc-200'}`} />
      </div>
    </div>
  );

  // Render Footer Buttons for 4 steps
  const renderFooter = () => (
    <div className="flex justify-between items-center pt-4 border-t border-zinc-200 mt-auto">
      <div>
        {step > 1 && (
          <Button
            variant="ghost"
            onClick={() => setStep(prev => prev - 1)}
            className="h-11 px-3 text-zinc-500 hover:text-zinc-800 text-sm flex items-center gap-1 shadow-none cursor-pointer"
          >
            <ChevronLeft className="h-4 w-4" /> Back
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2">
        {step === 1 && (
          <Button
            onClick={() => setStep(2)}
            className="h-11 px-5 font-semibold text-sm flex items-center gap-1.5 shadow-none bg-sahi-primary-color hover:opacity-90 text-white cursor-pointer"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        )}

        {step === 2 && (
          <Button
            onClick={handleSaveProfile}
            className="h-11 px-5 font-semibold text-sm flex items-center gap-1.5 shadow-none bg-sahi-primary-color hover:opacity-90 text-white cursor-pointer"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        )}

        {step === 3 && (
          <Button
            onClick={handleSaveTargets}
            className="h-11 px-5 font-semibold text-sm flex items-center gap-1.5 shadow-none bg-sahi-primary-color hover:opacity-90 text-white cursor-pointer"
          >
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        )}

        {step === 4 && (
          <>
            <Button
            variant="ghost"
              onClick={() => cleanupAndFinish()}
            className="h-11 px-3 text-zinc-500 hover:text-zinc-800 text-sm flex items-center gap-1 shadow-none cursor-pointer"
            >
              Skip
            </Button>
            <Button
              onClick={handlePublishIntroPost}
              disabled={postSubmitting}
              className="h-11 px-4 font-semibold text-sm flex items-center gap-1.5 shadow-none bg-sahi-primary-color hover:opacity-90 text-white cursor-pointer"
            >
              Post & Finish <Check className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );

  const renderContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 1 of 4</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">What do you provide?</h2>
            </div>

            <div className="space-y-3 overflow-y-auto flex-1 no-scrollbar pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              <style>{`
                @keyframes checkPop {
                  0% { transform: scale(0.4); opacity: 0; }
                  65% { transform: scale(1.18); opacity: 1; }
                  100% { transform: scale(1); opacity: 1; }
                }
                @keyframes iconPop {
                  0% { transform: scale(0.92); }
                  50% { transform: scale(1.08); }
                  100% { transform: scale(1); }
                }
              `}</style>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1">
                {relevantSegments.map((s) => {
                  const isSelected = Number(profileData.segmentID) === Number(s.SegmentID);
                  const IconComponent = SEGMENT_ICONS[s.SegmentID] || Layers;
                  return (
                    <button
                      key={s.SegmentID}
                      type="button"
                      onClick={() =>
                        setProfileData((prev) => ({
                          ...prev,
                          segmentID: s.SegmentID,
                          industryID: s.IndustryID || "",
                        }))
                      }
                      className={cn(
                        "w-full p-3 rounded-xl border-2 text-left flex items-center gap-3 transition-all duration-200 ease-out cursor-pointer select-none active:scale-[0.985] outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ring-0 ring-offset-0 group",
                        isSelected
                          ? "bg-emerald-50/60 text-zinc-900 shadow-xs"
                          : "bg-white border-zinc-200/0  text-zinc-700 shadow-2xs"
                      )}
                      style={{ borderColor: isSelected ? 'var(--sahi-primary-color, #0b5d1e)' : undefined }}
                    >
                      <div
                        className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200",
                          isSelected
                            ? "bg-sahi-primary-color text-white shadow-xs animate-[iconPop_0.25s_ease-out]"
                            : "bg-zinc-100 text-zinc-500"
                        )}
                      >
                        <IconComponent className="w-4 h-4" />
                      </div>

                      <span
                        className={cn(
                          "text-[13.5px] leading-tight flex-1 line-clamp-1",
                          isSelected ? "text-zinc-900 font-semibold" : "text-zinc-700 font-medium"
                        )}
                      >
                        {s.Segment}
                      </span>

                      {isSelected && (
                        <div className="w-5 h-5 rounded-full bg-sahi-primary-color text-white flex items-center justify-center shrink-0 shadow-xs animate-[checkPop_0.25s_cubic-bezier(0.175,0.885,0.32,1.275)_forwards]">
                          <Check className="w-3 h-3 stroke-[2.8]" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-4 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 2 of 4</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">Your Company Profile</h2>
            </div>

            <div className="space-y-5 overflow-y-auto flex-1 no-scrollbar pb-4" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {/* Logo upload field */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Company Logo</Label>
                <div className="flex flex-col items-start gap-3">
                  <div
                    onClick={() => {
                      if (!profileData.logoPreview) {
                        logoInputRef.current?.click();
                      } else {
                        setShowLogoActions(!showLogoActions);
                      }
                    }}
                    className="w-32 h-32 sahi-radius border border-dashed flex flex-col items-center justify-center overflow-hidden flex-shrink-0 transition-all duration-300 relative group cursor-pointer border-zinc-300 bg-white hover:bg-zinc-100/60 shadow-2xs"
                  >
                    {profileData.logoPreview ? (
                      <>
                        <img src={profileData.logoPreview} alt="Logo" className="w-full h-full object-contain p-1.5" />

                        {/* Modern Action Overlay */}
                        <div className={`absolute inset-0 bg-black/50 flex items-center justify-center gap-3 transition-opacity duration-300 ${showLogoActions ? 'opacity-100 pointer-events-auto' : 'opacity-0 group-hover:opacity-100 pointer-events-none group-hover:pointer-events-auto'
                          }`}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              logoInputRef.current?.click();
                              setShowLogoActions(false);
                            }}
                            className="p-2 bg-white hover:bg-zinc-100 text-zinc-800 rounded-full shadow-md transform hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center"
                            title="Change Logo"
                          >
                            <Upload className="w-5 h-5 text-[var(--sahi-green)]" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLogo();
                              setShowLogoActions(false);
                            }}
                            className="p-2 bg-white hover:bg-zinc-100 text-red-650 rounded-full shadow-md transform hover:scale-110 active:scale-95 transition-all duration-200 cursor-pointer flex items-center justify-center"
                            title="Remove Logo"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center justify-center p-2 text-center select-none">
                        <Upload className="w-5 h-5 text-[var(--sahi-green,#0b5d1e)] mb-1.5" />
                        <span className="text-[11px] font-bold text-zinc-700">Upload Logo</span>
                        <span className="text-[9px] text-zinc-500 mt-0.5">Max 2MB</span>
                      </div>
                    )}
                  </div>

                  {/* Hidden Input File Element */}
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </div>
              </div>

              {/* City & State */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-sm font-medium">City</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Ahmedabad"
                    value={profileData.city}
                    onChange={(e) => {
                      const val = e.target.value;
                      let matchedStateID = profileData.stateID;
                      let matchedStateName = profileData.state;

                      if (val) {
                        const trimmedCity = val.trim().toLowerCase();
                        const foundCityKey = Object.keys(cityToStateMap).find(key => key.toLowerCase() === trimmedCity);
                        if (foundCityKey) {
                          const stateNameFromMap = cityToStateMap[foundCityKey];
                          const matchedStateObj = states.find(s => s.State.toLowerCase() === stateNameFromMap.toLowerCase());
                          if (matchedStateObj) {
                            matchedStateID = matchedStateObj.StateID;
                            matchedStateName = matchedStateObj.State;
                          }
                        }
                      }

                      setProfileData(prev => ({
                        ...prev,
                        city: val,
                        stateID: matchedStateID,
                        state: matchedStateName
                      }));
                    }}
                    className="bg-white sahi-input h-11 text-sm shadow-xs"
                  />
                </div>

                <div className="space-y-1">
                  <Label className="text-sm font-medium">State</Label>
                  {profileData.countryID === 1 ? (
                    <CustomSearchableDropdown
                      fieldID="state"
                      placeholder="Select State"
                      options={stateOptions}
                      value={profileData.stateID}
                      onSelect={(val) => {
                        const matchedState = states.find(s => s.StateID === Number(val));
                        setProfileData(prev => ({
                          ...prev,
                          stateID: Number(val),
                          state: matchedState ? matchedState.State : ""
                        }));
                      }}
                      className="w-full bg-white h-11 text-sm shadow-xs"
                    />
                  ) : (
                    <Input
                      type="text"
                      placeholder="e.g. State / Region"
                      value={profileData.state}
                      onChange={(e) => {
                        setProfileData(prev => ({
                          ...prev,
                          stateID: 0,
                          state: e.target.value
                        }));
                      }}
                      className="bg-white sahi-input h-11 text-sm shadow-xs"
                    />
                  )}
                </div>
              </div>

              {/* Country */}
              <div className="space-y-1">
                <Label className="text-sm font-medium">Country</Label>
                <CustomSearchableDropdown
                  fieldID="country"
                  placeholder="Select Country"
                  options={countryOptions}
                  value={profileData.countryID}
                  onSelect={(val) => {
                    const matchedCountry = countries.find(c => c.CountryID === Number(val));
                    setProfileData(prev => ({
                      ...prev,
                      countryID: Number(val),
                      country: matchedCountry ? matchedCountry.Country : "",
                      ...(Number(val) !== 1 ? { stateID: 0, state: "" } : {})
                    }));
                  }}
                  className="w-full bg-white h-11 text-sm shadow-xs"
                />
              </div>

            </div>
          </div>
        );

      case 3: {
        const currentSegmentObj = segments.find(s => Number(s.SegmentID) === Number(profileData.segmentID));
        const buyFromIDs = currentSegmentObj?.BuyFrom || [];
        const sellToIDs = currentSegmentObj?.SellTo || [];

        // Prefill strictly from the other 7 segments in relevantSegments
        const other7Segments = relevantSegments.filter(s => Number(s.SegmentID) !== Number(profileData.segmentID));
        const buyFromSegments = other7Segments
          .filter(s => buyFromIDs.includes(Number(s.SegmentID)))
          .filter(s => !removedBuyFromIDs.includes(Number(s.SegmentID)));
        const sellToSegments = other7Segments
          .filter(s => sellToIDs.includes(Number(s.SegmentID)))
          .filter(s => !removedSellToIDs.includes(Number(s.SegmentID)));

        // Combined initial segments + dynamically added ones
        const allBuyFromSegments = [
          ...buyFromSegments,
          ...extraBuyFromSegments.filter(extra => !buyFromSegments.some(s => Number(s.SegmentID) === Number(extra.SegmentID)))
        ];

        const allSellToSegments = [
          ...sellToSegments,
          ...extraSellToSegments.filter(extra => !sellToSegments.some(s => Number(s.SegmentID) === Number(extra.SegmentID)))
        ];

        // [+ Add] dropdown options show ALL segments formatted as "Industry > Segment"
        const availableBuyFromOptions = segments
          .filter(s => Number(s.SegmentID) !== Number(profileData.segmentID))
          .filter(s => !allBuyFromSegments.some(item => Number(item.SegmentID) === Number(s.SegmentID)))
          .map(s => ({
            value: s.SegmentID,
            label: s.Industry ? `${s.Industry} > ${s.Segment}` : s.Segment
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        const availableSellToOptions = segments
          .filter(s => Number(s.SegmentID) !== Number(profileData.segmentID))
          .filter(s => !allSellToSegments.some(item => Number(item.SegmentID) === Number(s.SegmentID)))
          .map(s => ({
            value: s.SegmentID,
            label: s.Industry ? `${s.Industry} > ${s.Segment}` : s.Segment
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        return (
          <div className="space-y-4 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 3 of 4</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">Your Supply Chain</h2>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
               This helps us match you with the right buyers & suppliers.
              </p>
            </div>

            <div className="space-y-6 overflow-y-auto flex-1 pb-4">
              {/* Buy From Section */}
              <div className="space-y-2.5">
                <Label className="text-sm font-semibold text-zinc-800">Who do you buy from?</Label>

                {/* Chip list */}
                <div className="flex flex-wrap items-center gap-2">
                  {allBuyFromSegments.map(s => {
                    const isExtra = extraBuyFromSegments.some(extra => Number(extra.SegmentID) === Number(s.SegmentID));
                    return (
                      <div
                        key={s.SegmentID}
                        className="inline-flex items-center justify-center h-8 text-[13px] font-medium pl-3 pr-2 rounded-[5px] bg-white text-zinc-700 select-none whitespace-nowrap gap-1.5 transition-colors border border-zinc-200"
                      >
                        <span className="leading-none">{s.Segment}</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (isExtra) {
                              setExtraBuyFromSegments(prev => prev.filter(item => Number(item.SegmentID) !== Number(s.SegmentID)));
                            } else {
                              setRemovedBuyFromIDs(prev => [...prev, Number(s.SegmentID)]);
                            }
                          }}
                          className="p-0.5 rounded-full hover:bg-zinc-300 hover:text-zinc-900 text-zinc-400 transition-colors cursor-pointer"
                          title="Remove segment"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* + Add row on next line with inline dropdown */}
                <div data-inline-segment-dropdown className="flex items-center gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={() => setActiveDropdown(prev => prev === 'buyFrom' ? null : 'buyFrom')}
                    className={cn(
                      "inline-flex items-center justify-center h-8 text-[13px] font-medium px-3 rounded-[5px] border border-dashed border-[#9ca3af] text-[#6b7280] bg-transparent hover:bg-gray-50 hover:text-zinc-800 hover:border-zinc-400 box-border transition-all duration-200 cursor-pointer select-none whitespace-nowrap active:scale-[0.98] shrink-0",
                      activeDropdown === 'buyFrom' && !isMobileDropdown && "hidden"
                    )}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    <span className="leading-none">Add</span>
                  </button>

                  {activeDropdown === 'buyFrom' && (
                    <div
                      onBlur={closeInlineDropdownOnBlur}
                      className={cn(
                        isMobileDropdown
                          ? "hidden"
                          : "w-64 max-w-[calc(100%-80px)] [&_[role=combobox]]:!min-h-8 [&_[role=combobox]]:!h-8 [&_[role=combobox]]:text-xs [&_[role=combobox]_span]:text-xs [&_[role=combobox]]:rounded-[5px]"
                      )}
                    >
                      <CustomSearchableDropdown
                        options={availableBuyFromOptions}
                        placeholder="Search segment..."
                        searchPlaceholder="Search segments..."
                        openDialog={true}
                        onClose={() => setActiveDropdown(null)}
                        onSelect={(segID) => {
                          if (segID) {
                            const numID = Number(segID);
                            setRemovedBuyFromIDs(prev => prev.filter(id => id !== numID));
                            const matchedSeg = segments.find(s => Number(s.SegmentID) === numID);
                            if (matchedSeg) {
                              setExtraBuyFromSegments(prev => {
                                if (prev.some(item => Number(item.SegmentID) === numID)) return prev;
                                return [...prev, matchedSeg];
                              });
                            }
                          }
                          setActiveDropdown(null);
                        }}
                        className="w-full text-xs"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Sell To Section */}
              <div className="space-y-2.5">
                <Label className="text-sm font-semibold text-zinc-800">Who do you sell to?</Label>

                {/* Chip list */}
                <div className="flex flex-wrap items-center gap-2">
                  {allSellToSegments.map(s => {
                    const isExtra = extraSellToSegments.some(extra => Number(extra.SegmentID) === Number(s.SegmentID));
                    return (
                      <div
                        key={s.SegmentID}
                        className="inline-flex items-center justify-center h-8 text-[13px] font-medium pl-3 pr-2 rounded-[5px] bg-white text-zinc-700 select-none whitespace-nowrap gap-1.5 transition-colors border border-zinc-200"
                      >
                        <span className="leading-none">{s.Segment}</span>
                        <button
                          type="button"
                          onClick={() => {
                            if (isExtra) {
                              setExtraSellToSegments(prev => prev.filter(item => Number(item.SegmentID) !== Number(s.SegmentID)));
                            } else {
                              setRemovedSellToIDs(prev => [...prev, Number(s.SegmentID)]);
                            }
                          }}
                          className="p-0.5 rounded-full hover:bg-zinc-300 hover:text-zinc-900 text-zinc-400 transition-colors cursor-pointer"
                          title="Remove segment"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* + Add row on next line with inline dropdown */}
                <div data-inline-segment-dropdown className="flex items-center gap-2 pt-0.5">
                  <button
                    type="button"
                    onClick={() => setActiveDropdown(prev => prev === 'sellTo' ? null : 'sellTo')}
                    className={cn(
                      "inline-flex items-center justify-center h-8 text-[13px] font-medium px-3 rounded-[5px] border border-dashed border-[#9ca3af] text-[#6b7280] bg-transparent hover:bg-gray-50 hover:text-zinc-800 hover:border-zinc-400 box-border transition-all duration-200 cursor-pointer select-none whitespace-nowrap active:scale-[0.98] shrink-0",
                      activeDropdown === 'sellTo' && !isMobileDropdown && "hidden"
                    )}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    <span className="leading-none">Add</span>
                  </button>

                  {activeDropdown === 'sellTo' && (
                    <div
                      onBlur={closeInlineDropdownOnBlur}
                      className={cn(
                        isMobileDropdown
                          ? "hidden"
                          : "w-64 max-w-[calc(100%-80px)] [&_[role=combobox]]:!min-h-8 [&_[role=combobox]]:!h-8 [&_[role=combobox]]:text-xs [&_[role=combobox]_span]:text-xs [&_[role=combobox]]:rounded-[5px]"
                      )}
                    >
                      <CustomSearchableDropdown
                        options={availableSellToOptions}
                        placeholder="Search segment..."
                        searchPlaceholder="Search segments..."
                        openDialog={true}
                        onClose={() => setActiveDropdown(null)}
                        onSelect={(segID) => {
                          if (segID) {
                            const numID = Number(segID);
                            setRemovedSellToIDs(prev => prev.filter(id => id !== numID));
                            const matchedSeg = segments.find(s => Number(s.SegmentID) === numID);
                            if (matchedSeg) {
                              setExtraSellToSegments(prev => {
                                if (prev.some(item => Number(item.SegmentID) === numID)) return prev;
                                return [...prev, matchedSeg];
                              });
                            }
                          }
                          setActiveDropdown(null);
                        }}
                        className="w-full text-xs"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 4:
        return (
          <div className="space-y-4 text-left flex flex-col h-full overflow-hidden">
            <div>
              <span className="text-[11px] font-bold text-sahi-primary-color tracking-wider uppercase">Step 4 of 4</span>
              <h2 className="text-2xl font-bold text-zinc-800 mt-0.5">Introduce Yourself</h2>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                Your first post to the Sahi Community.
              </p>
            </div>

            <div className="space-y-4 overflow-y-auto flex-1 pb-4">
              <div className="bg-[#edf7ed] border border-[#d6ebd7] sahi-radius px-3.5 py-3 flex items-start gap-2.5">
                <Sparkles className="w-4 h-4 text-[#166534] shrink-0 mt-0.5" />
                <span className="text-xs sm:text-[13px] font-medium text-[#165a27] leading-snug">
                  Drafted for you from what you&apos;ve shared, just review &amp; post.
                </span>
              </div>

              {/* Post Title */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-zinc-700">Post Title</Label>
                <Input
                  value={postTitle}
                  onChange={(e) => {
                    setPostTitle(e.target.value);
                    setHasCustomPostTitle(true);
                  }}
                  placeholder="e.g. Introducing Our Company — Quality PCB & EMS Solutions"
                  className="bg-white sahi-input h-10 text-sm shadow-xs"
                />
              </div>

              {/* Post Details */}
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-zinc-700">About Your Business</Label>
                <Textarea
                  value={postDetail}
                  onChange={(e) => {
                    setPostDetail(e.target.value);
                    setHasCustomPostDetail(true);
                  }}
                  rows={4}
                  placeholder="Tell businesses about your capabilities, manufacturing capacity, certifications, and what partnerships you are looking for..."
                  className="bg-white sahi-input min-h-[90px] text-sm shadow-xs"
                />
              </div>

              {/* Post Image */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-zinc-700">Attach Photo</Label>
                  {postImagePreview && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => postImageInputRef.current?.click()}
                        className="text-zinc-400 hover:text-zinc-600 hover:underline cursor-pointer transition-colors"
                      >
                        Change
                      </button>
                      <span className="text-zinc-300">|</span>
                      <button
                        type="button"
                        onClick={() => {
                          setHasCustomPostImage(true);
                          setPostImageFile(null);
                          setPostImagePreview(null);
                          if (postImageInputRef.current) postImageInputRef.current.value = "";
                        }}
                        className="text-zinc-400 hover:text-zinc-600 hover:underline cursor-pointer transition-colors"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                <div className="w-full">
                  {postImagePreview ? (
                    <div
                      onClick={() => postImageInputRef.current?.click()}
                      className="relative w-full h-44 sm:h-52 sahi-radius border border-gray-300 overflow-hidden bg-zinc-100 flex items-center justify-center shadow-xs cursor-pointer group"
                      title="Click to change photo"
                    >
                      <img src={postImagePreview} alt="Post preview" className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.01]" />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => postImageInputRef.current?.click()}
                      className="w-full border border-dashed border-zinc-300 hover:border-sahi-primary-color sahi-radius p-4 sm:p-5 flex flex-col items-center justify-center gap-1.5 text-zinc-600 hover:text-zinc-800 transition-all bg-white hover:bg-emerald-50/20 cursor-pointer shadow-2xs group"
                    >
                      <div className="w-9 h-9 rounded-full bg-zinc-100 group-hover:bg-emerald-100/60 flex items-center justify-center text-zinc-500 group-hover:text-sahi-primary-color transition-colors">
                        <ImageIcon className="w-4 h-4" />
                      </div>
                      <div className="text-center">
                        <span className="text-xs font-semibold text-zinc-800 block">Showcase your products, factory, or brand!</span>
                        <span className="text-[11px] text-zinc-400">PNG, JPG up to 5MB</span>
                      </div>
                    </button>
                  )}
                  <input
                    ref={postImageInputRef}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp"
                    className="hidden"
                    onChange={handlePostImageChange}
                  />
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getDialogWidth = () => {
    return 'sm:!max-w-[620px]';
  };

  if (!isOpen) return null;

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[90] bg-background flex flex-col h-full w-full p-5 overflow-hidden">
        <ProgressBar />
        <div key={step} className="flex-1 flex flex-col overflow-hidden">
          {renderContent()}
        </div>
        {renderFooter()}
      </div>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      const needsOnboarding = localStorage.getItem("needs_onboarding") === "1";
      if (!open && needsOnboarding) return;
      cleanupAndFinish();
    }}>
      <DialogContent
        onEscapeKeyDown={(e) => {
          if (localStorage.getItem("needs_onboarding") === "1") {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (localStorage.getItem("needs_onboarding") === "1") {
            e.preventDefault();
          }
        }}
        className={`w-full ${getDialogWidth()} min-h-[500px] p-6 bg-background border border-zinc-200 shadow-xl rounded-2xl overflow-hidden flex flex-col justify-between transition-all duration-300 ease-in-out`}
      >
        <ProgressBar />
        <div key={step} className="flex-1 flex flex-col overflow-hidden py-2">
          {renderContent()}
        </div>
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
};

export default SegmentOnboarding;
